/**
 * Sharing rendered images (receipts, ID cards) from inside the React Native app.
 *
 * Android WebView implements neither the Web Share API nor file downloads, so a
 * blob can never leave the page on its own. The image is handed to the host app
 * over `postMessage` instead, and the app opens the native share sheet.
 *
 * Contract:
 *   WebView -> RN : { type: 'SHARE_IMAGE_REQUEST', requestId,
 *                     images: [{ base64, mimeType, fileName }] }
 *   RN -> WebView : { type: 'SHARE_IMAGE_RESULT', requestId, status: 'shared' | 'cancelled' }
 *                   { type: 'SHARE_IMAGE_ERROR', requestId, error }
 *
 * See docs/WHATSAPP_IMAGE_SHARE_WEBVIEW.md for the React Native side.
 */

export const isReactNativeWebView = () =>
  typeof window !== 'undefined' && !!window.ReactNativeWebView

const NATIVE_SHARE_TIMEOUT_MS = 60000

// An app build without the SHARE_IMAGE_REQUEST handler never answers, so stop
// waiting once it is clear the native share sheet never opened.
const NATIVE_SHARE_PROBE_MS = 9000

// The bridge carries the image as a base64 string, and Android silently drops
// oversized postMessage payloads, so keep it well under a megabyte.
const MAX_IMAGE_CHARS = 600000
const MAX_IMAGE_EDGE = 1400
const JPEG_QUALITY_STEPS = [0.85, 0.7, 0.55, 0.4]

const downscaleCanvas = (canvas, maxEdge) => {
  const longestEdge = Math.max(canvas.width, canvas.height)
  if (!longestEdge || longestEdge <= maxEdge) return canvas

  const ratio = maxEdge / longestEdge
  const scaled = document.createElement('canvas')
  scaled.width = Math.max(1, Math.round(canvas.width * ratio))
  scaled.height = Math.max(1, Math.round(canvas.height * ratio))
  scaled.getContext('2d').drawImage(canvas, 0, 0, scaled.width, scaled.height)
  return scaled
}

export const canvasToShareImage = (
  canvas,
  baseFileName,
  { preferJpeg = false } = {},
) => {
  const source = downscaleCanvas(canvas, MAX_IMAGE_EDGE)
  let dataUrl = preferJpeg ? '' : source.toDataURL('image/png')
  let mimeType = 'image/png'
  let extension = 'png'

  if (preferJpeg || dataUrl.length > MAX_IMAGE_CHARS) {
    for (const quality of JPEG_QUALITY_STEPS) {
      dataUrl = source.toDataURL('image/jpeg', quality)
      mimeType = 'image/jpeg'
      extension = 'jpg'
      if (dataUrl.length <= MAX_IMAGE_CHARS) break
    }
  }

  // Old WebViews return "data:," when the canvas is too large to encode.
  const base64 = dataUrl.startsWith('data:image')
    ? dataUrl.slice(dataUrl.indexOf(',') + 1)
    : ''
  if (!base64) throw new Error('Could not render the image for sharing')

  return {
    mimeType,
    base64,
    fileName: `${baseFileName}.${extension}`,
  }
}

const waiters = new Map()
let listening = false

const parseNativePayload = (raw) => {
  let payload = raw
  if (typeof payload === 'string') {
    try {
      payload = JSON.parse(payload)
    } catch (_) {
      return null
    }
  }
  return payload && typeof payload === 'object' ? payload : null
}

const settleWaiter = (payload, result) => {
  const waiter =
    (payload.requestId && waiters.get(payload.requestId)) ||
    (waiters.size === 1 ? waiters.values().next().value : null)
  if (waiter) waiter(result)
}

const handleNativeMessage = (event) => {
  const payload = parseNativePayload(event?.data)
  if (!payload) return

  if (payload.type === 'SHARE_IMAGE_RESULT') {
    settleWaiter(payload, { status: payload.status || 'shared' })
  } else if (payload.type === 'SHARE_IMAGE_ERROR') {
    settleWaiter(payload, { status: 'error', error: payload.error })
  }
}

const startListening = () => {
  if (listening || typeof window === 'undefined') return
  listening = true

  // RN can also deliver the reply directly with injectJavaScript.
  window.onNativeShareImage = (payload) => handleNativeMessage({ data: payload })

  // iOS RN WebView fires on `window`, Android often fires on `document`.
  window.addEventListener('message', handleNativeMessage)
  document.addEventListener('message', handleNativeMessage)
}

/**
 * The share sheet is a native screen, so the WebView loses focus/visibility
 * while it is up. That is the only signal we get that the host app actually
 * handled the request.
 */
const watchNativeTakeover = () => {
  const watcher = { handled: false, stop: () => {} }
  if (typeof window === 'undefined') return watcher

  const mark = () => {
    if (document.visibilityState !== 'visible' || document.hidden) {
      watcher.handled = true
    }
  }
  const markBlur = () => {
    watcher.handled = true
  }

  window.addEventListener('blur', markBlur)
  window.addEventListener('pagehide', markBlur)
  document.addEventListener('visibilitychange', mark)

  watcher.stop = () => {
    window.removeEventListener('blur', markBlur)
    window.removeEventListener('pagehide', markBlur)
    document.removeEventListener('visibilitychange', mark)
  }
  return watcher
}

/**
 * Resolves with { status: 'shared' | 'cancelled' | 'unsupported' | 'error' | 'timeout' }.
 * `unsupported` means the host app never picked the message up, so the caller
 * should fall back to a plain WhatsApp link.
 */
export const requestNativeImageShare = (images) =>
  new Promise((resolve) => {
    startListening()

    const requestId = `share-${Date.now()}-${Math.random()
      .toString(16)
      .slice(2)}`
    const takeover = watchNativeTakeover()
    let probeTimer = null
    let hardTimer = null

    const finish = (result) => {
      clearTimeout(probeTimer)
      clearTimeout(hardTimer)
      takeover.stop()
      waiters.delete(requestId)
      resolve(result)
    }

    waiters.set(requestId, finish)

    probeTimer = setTimeout(() => {
      if (!takeover.handled) finish({ status: 'unsupported' })
    }, NATIVE_SHARE_PROBE_MS)

    hardTimer = setTimeout(
      () => finish({ status: 'timeout' }),
      NATIVE_SHARE_TIMEOUT_MS,
    )

    try {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({ type: 'SHARE_IMAGE_REQUEST', requestId, images }),
      )
    } catch (error) {
      finish({ status: 'error', error: error?.message })
    }
  })

export const nativeShareErrorMessage = (result) => {
  if (result?.error) return result.error
  if (result?.status === 'unsupported') {
    return 'This app version cannot attach images to WhatsApp'
  }
  if (result?.status === 'timeout') {
    return 'The app did not respond to the share request'
  }
  return 'Could not share the image'
}

/**
 * Last resort inside the app: WhatsApp accepts a pasted image in the chat box,
 * and the clipboard is the only file-free way to hand the image over. Needs a
 * recent WebView and page focus, so treat failure as normal.
 */
export const copyImageToClipboard = async (blob) => {
  try {
    if (
      !blob ||
      typeof navigator === 'undefined' ||
      !navigator.clipboard?.write ||
      typeof window.ClipboardItem !== 'function'
    ) {
      return false
    }
    await navigator.clipboard.write([
      new window.ClipboardItem({ [blob.type || 'image/png']: blob }),
    ])
    return true
  } catch (error) {
    return false
  }
}

/** Resolves with 'shared' | 'cancelled' | 'unsupported'. */
export const shareFilesWithWebShare = async (files) => {
  const shareData = { files }
  if (
    typeof navigator === 'undefined' ||
    !navigator.canShare ||
    !navigator.share ||
    !navigator.canShare(shareData)
  ) {
    return 'unsupported'
  }
  try {
    await navigator.share(shareData)
    return 'shared'
  } catch (error) {
    if (error?.name === 'AbortError') return 'cancelled'
    return 'unsupported'
  }
}

export const whatsAppUrl = (phone = '', text = '') => {
  const base = phone ? `https://wa.me/${phone}` : 'https://wa.me/'
  return text ? `${base}?text=${encodeURIComponent(text)}` : base
}

/**
 * Same link PendingFee uses: opens WhatsApp and lets the user pick any chat.
 * This is the one WhatsApp route that works in the app, because the host app
 * hands `wa.me` links to the OS instead of loading them in the WebView.
 */
export const openWhatsApp = (phone = '', text = '') => {
  window.open(whatsAppUrl(phone, text), '_blank', 'noopener,noreferrer')
}

/**
 * True once WhatsApp (or a new tab) took over the screen. Both the WebView and
 * desktop browsers refuse `window.open` when the click that started the share
 * has already been spent on awaiting the image, and the call fails silently —
 * this is how the caller notices and offers a fresh tap instead.
 */
export const whatsAppOpened = () =>
  typeof document === 'undefined' || document.visibilityState !== 'visible'
